/**
 * SSE урсгал — борлуулалт бүртгэгдэхэд мэдэгдэнэ.
 *
 * ⚠️ `EventSource` АШИГЛАХГҮЙ. Access token нь Bearer header-ээр явдаг
 *    бөгөөд `EventSource` header тавьж чаддаггүй. Токеныг URL-д
 *    тавибал сервер, proxy-ийн лог, хөтчийн түүхэнд үлдэж «токен
 *    зөвхөн санах ойд» гэсэн шийдвэр (`lib/api.ts`) утгагүй болно.
 *    Оронд нь `fetch` + `ReadableStream` — утсан дээр яг ижил
 *    `text/event-stream`, гэхдээ header явуулж чадна.
 *
 * ⚠️ Таб далд болоход урсгалыг ЗОГСООНО. Арван нээлттэй таб бүр
 *    сервер дээр нэг холболт барих нь дэмий. Буцаж ирэхэд БҮТЭН
 *    татац хийнэ — далд байх хугацаанд алдсан event-ийг нөхнө.
 */

import { useEffect, useRef, useState } from 'react';
import { getAccessToken } from './api';

export type StreamStatus = 'connecting' | 'live' | 'offline';

/** Дахин холбогдох хүлээлт: 1s → 2s → 4s → … → 30s дээр тогтоно. */
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

/** Урсгал ажиллахгүй үеийн нөөц — 30 секунд тутам бүтэн татац. */
const POLL_MS = 30_000;

interface Options {
  onSale: (amountMnt: string, occurredAt: string) => void;
  /** Холболт сэргэх, таб буцаж идэвхжих үед бүтэн татац */
  onResync: () => void;
}

export function useRevenueStream({ onSale, onResync }: Options): StreamStatus {
  const [status, setStatus] = useState<StreamStatus>('connecting');

  // Handler-ууд render бүрд шинэ функц болдог тул ref-ээр барина —
  // эс тэгвээс effect дахин ажиллаж холболт тасарна
  const cb = useRef({ onSale, onResync });
  cb.current = { onSale, onResync };

  useEffect(() => {
    let stopped = false;
    let attempt = 0;
    let abort: AbortController | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    const stopPolling = () => {
      clearInterval(pollTimer);
      pollTimer = undefined;
    };

    /**
     * Одоо байгаа холболт ба хүлээгдэж буй дахин оролдлогыг зогсооно.
     *
     * ⚠️ ШИНЭ ХОЛБОЛТ НЭЭХИЙН ӨМНӨ ЗААВАЛ ДУУДНА. Эс тэгвээс `abort`
     *    хувьсагч шинэ controller-оор дарагдаж, хуучин холболт хэнд ч
     *    хамаагүй нээлттэй үлдэнэ — сервер дээр хэдэн ч холболт
     *    хуримтлагдана. Ялангуяа: retry timer хүлээж байхад таб буцаж
     *    идэвхжих үед хоёр `connect()` зэрэг ажиллана.
     */
    const closeCurrent = () => {
      clearTimeout(retryTimer);
      retryTimer = undefined;
      abort?.abort();
      abort = null;
    };

    /** Урсгал унасан үед — 30 секунд тутам бүтэн татац */
    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => cb.current.onResync(), POLL_MS);
    };

    const scheduleRetry = () => {
      if (stopped) return;
      const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? POLL_MS;
      attempt += 1;
      startPolling();
      retryTimer = setTimeout(() => void connect(), wait);
    };

    async function connect(): Promise<void> {
      if (stopped || document.visibilityState === 'hidden') return;

      // Нэг агшинд ЯГ НЭГ холболт
      closeCurrent();
      const ctl = new AbortController();
      abort = ctl;
      setStatus('connecting');

      try {
        const token = getAccessToken();
        const res = await fetch('/api/v1/reports/revenue/stream', {
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          // Өөрийн controller-оо барина — `abort` энэ хооронд
          // өөр холболтод шилжсэн байж болно
          signal: ctl.signal,
        });

        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);

        // Холбогдлоо — өмнө тасарсан бол алдсан үеийг нөхнө
        if (attempt > 0) cb.current.onResync();
        attempt = 0;
        stopPolling();
        setStatus('live');

        await readEvents(res.body, (event, data) => {
          if (event !== 'revenue.updated') return;
          const p = JSON.parse(data) as { amountMnt?: string; occurredAt?: string };
          if (p.amountMnt && p.occurredAt) cb.current.onSale(p.amountMnt, p.occurredAt);
        });

        // Сервер холболтыг хаалаа — дахин холбогдоно.
        // Энэ хооронд өөр холболт нээгдсэн бол хөндөхгүй.
        if (!stopped && abort === ctl) {
          setStatus('offline');
          scheduleRetry();
        }
      } catch (err) {
        if (stopped || ctl.signal.aborted) return;
        setStatus('offline');
        scheduleRetry();
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        closeCurrent();
        stopPolling();
        setStatus('offline');
      } else {
        attempt = 0;
        cb.current.onResync(); // далд байх үед алдсаныг нөхнө
        void connect(); // closeCurrent() нь connect() дотор дуудагдана
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    void connect();

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      closeCurrent();
      stopPolling();
    };
  }, []);

  return status;
}

/**
 * `text/event-stream`-ийг задлана.
 *
 * SSE-ийн хүрээ нь хоосон мөрөөр тусгаарлагдана. Нэг frame нь
 * `event:` ба `data:` мөрүүдтэй. `:` -ээр эхэлсэн мөр нь тайлбар
 * (сэтгэл) — алгасна.
 */
async function readEvents(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;

    buf += decoder.decode(value, { stream: true });

    let sep = buf.indexOf('\n\n');
    while (sep !== -1) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);

      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith(':')) continue;
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (data) onEvent(event, data);

      sep = buf.indexOf('\n\n');
    }
  }
}
